import type { SiteGraph, SiteNode } from '../../types';

/**
 * Turning the site graph into what a chord diagram needs.
 *
 * Dependency-free, like `linkFacts` and for the same reason: CRA's jest
 * cannot load `d3-chord` (ESM-only, `"type": "module"`), so anything that needs
 * a test has to sit outside the component that imports it. d3 does layout maths
 * here and nothing else — React renders every element — which is also what
 * keeps this file testable at all.
 *
 * WHY A NODE-LINK DIAGRAM AND NOT A CHORD OR A SANKEY.
 *
 * A sankey assumes a DAG, and sites linking to each other is the thing this
 * feature exists to CREATE, so a cycle makes it throw or lay out nonsense.
 *
 * A chord was tried and was worse in practice. Chords are for DENSE
 * many-to-many flow; a real map here was seven sites and two links, and the
 * ring spent all its area on arcs that meant nothing while seven labels piled
 * into an unreadable stack at the top. Sparse graphs need nodes and arrows —
 * you can see a count on an arrow, and you can see a node with nothing
 * attached to it, which is the most useful thing on the screen.
 *
 * The layout is DETERMINISTIC, not a force simulation. At this size a
 * simulation buys nothing and costs everything that matters: nodes land
 * somewhere different on every load, labels drift into each other, and the
 * picture you looked at yesterday is not the one you get today.
 */

/** The bucket for pages with no deployment plan. Null is a REAL site here — an
 *  unwritten page is usually exactly this — so it needs a key, not a filter. */
export const NO_SITE = 'none';

export function siteKey(siteId: number | null): string {
  return siteId === null ? NO_SITE : String(siteId);
}

export interface Segment {
  fromSiteId: number | null;
  toSiteId: number | null;
  fromName: string;
  toName: string;
  count: number;
}

/**
 * What the panel should actually render.
 *
 * ONLY `no-sources` hides the graph. The other two used to as well — "there is
 * no shape to show" — and that was wrong once this became the surface you work
 * on: the pages are opened by clicking a node, so hiding the nodes until links
 * exist makes the first link impossible to create, and Clear links dropped you
 * straight into that dead end. They are kept apart because the CAPTION differs;
 * the picture does not.
 */
export type GraphState = 'no-sources' | 'no-links' | 'single-site' | 'ok';

export function graphState(graph: SiteGraph | null): GraphState {
  if (!graph || graph.sites.length === 0) return 'no-sources';
  if (graph.total_links === 0) return 'no-links';
  if (graph.sites.length === 1) return 'single-site';
  return 'ok';
}

/**
 * The imbalance on one site, in words.
 *
 * Two numbers side by side are a comparison the reader has to do. A site that
 * sends 40 links and receives 3 is the finding this whole view exists to
 * surface, and it should not depend on noticing that 40 > 3.
 */
export function balanceLabel(site: SiteNode): string {
  const { outbound: out, inbound: incoming } = site;
  if (out === 0 && incoming === 0) return 'no links either way';
  if (incoming === 0) return `sends ${out}, receives none`;
  if (out === 0) return `receives ${incoming}, sends none`;
  const ratio = out / incoming;
  if (ratio >= 3) return `sends ${out}, receives only ${incoming}`;
  if (ratio <= 1 / 3) return `receives ${incoming}, sends only ${out}`;
  return `sends ${out}, receives ${incoming}`;
}

/** "12 links" / "1 link" — used in three places, none of which should own it. */
export function linkCount(n: number): string {
  return `${n} link${n === 1 ? '' : 's'}`;
}

/**
 * The share of links that cross a site boundary, as a percentage string.
 *
 * The headline number for this whole feature — links between your own sites are
 * the reason to put several subjects in one map — and a raw count cannot say
 * whether 12 is a lot. Null when there are no links, rather than 0%, because
 * "0% of nothing" reads as a failure and there is simply nothing yet.
 */
export function crossingShare(graph: SiteGraph): string | null {
  if (graph.total_links === 0) return null;
  return `${Math.round((graph.crossing_links / graph.total_links) * 100)}%`;
}

/**
 * Colours for the ring — HEX, and a SEPARATE palette from `linkSourceColors`.
 *
 * Two reasons, both load-bearing. SVG `fill` cannot take a Tailwind class, so
 * the source palette is unusable here whatever else is true. And the axes are
 * different: `sourceColors` is keyed on a scope's `order`, but a site can hold
 * pages from several scopes and a scope can span several sites. Reusing those
 * colours would put the same indigo on two groupings that do not coincide,
 * which is worse than an unrelated palette — it would read as a relationship.
 *
 * Red and amber stay out for the reason they do in the source palette: they
 * carry state meanings elsewhere in this feature (never published / out of
 * scope) and a site must not accidentally wear one.
 */
export const SITE_COLORS = [
  // DEEP (Tailwind 700), and starting at a different point in the wheel than
  // the source palette's mid-tones (500).
  //
  // This started out as the same six hues in the same order, which was the
  // worst possible answer: the first chip and the first node were the identical
  // indigo while meaning entirely unrelated things, so the screen asserted a
  // correspondence that does not exist. Two rules keep them apart — sources are
  // mid-tone and sites are deep, and no position shares a hue — so the palettes
  // read as two families rather than one mapping.
  '#0f766e', // teal-700
  '#a21caf', // fuchsia-700
  '#1d4ed8', // blue-700
  '#4d7c0f', // lime-700
  '#be185d', // pink-700
  '#6d28d9', // violet-700
  '#0e7490', // cyan-700
  '#047857', // emerald-700
];

/**
 * The source palette's dots, as hex.
 *
 * Duplicated here rather than imported because `linkSourceColors` holds Tailwind
 * CLASS NAMES — `bg-indigo-500` — which cannot be compared to an SVG fill. The
 * only job of this list is to let a test prove the two palettes have not drifted
 * back into agreeing.
 */
export function sourceHex(order: number): string {
  return SOURCE_DOT_HEXES[order % SOURCE_DOT_HEXES.length];
}

/**
 * Which sites a subject's pages actually landed on.
 *
 * THE QUESTION THE SCREEN COULD NOT ANSWER. The chips across the top are
 * subjects; the nodes below are sites. They are different groupings — a subject
 * spans several sites and a site holds pages from several subjects — so nothing
 * on screen said which chip fed which node, and the only way to find out was to
 * know already.
 *
 * Returns site KEYS rather than ids so the "no site yet" bucket, whose id is
 * null, is addressable like any other.
 */
export function sitesForScope(
  pages: { scope_id: number; site_id: number | null }[],
  scopeId: number,
): Set<string> {
  const out = new Set<string>();
  for (const page of pages) {
    if (page.scope_id === scopeId) out.add(siteKey(page.site_id));
  }
  return out;
}

/**
 * Which subjects feed a site — the inverse of `sitesForScope`.
 *
 * WHY BOTH DIRECTIONS EXIST. Removing a subject chip does not always remove a
 * node, because a site can be fed by several subjects; on a real map two
 * sources produced four sites. Without this, a node that correctly stayed put
 * is indistinguishable from a panel that failed to refresh — and the second is
 * what anyone will assume.
 *
 * Ordered by scope order, so the dots come out in the same sequence as the
 * chips they refer to.
 */
export function scopesForSite(
  pages: { scope_id: number; site_id: number | null }[],
  site: number | null,
): number[] {
  const key = siteKey(site);
  const out = new Set<number>();
  for (const page of pages) {
    if (siteKey(page.site_id) === key) out.add(page.scope_id);
  }
  return Array.from(out).sort((a, b) => a - b);
}

export interface SiteFallout {
  /** Sites that lose their last page and vanish from the graph. */
  disappearing: string[];
  /** Sites that stay, because another subject also has pages there. */
  shrinking: string[];
}

/**
 * What removing a subject does to the SITES on the graph.
 *
 * The surprise this exists to prevent, verbatim from a real session: the glow
 * said "Stargazer's Utah has pages on these 2 sites", the subject was removed,
 * and both sites were still there. Nothing had failed — one kept a page from
 * Life Insurance and the other from a third subject, so their counts dropped
 * and they stayed. But "I removed the thing that fed it and it is still here"
 * reads as a bug every time, and the only way to know otherwise was to compare
 * page counts before and after.
 *
 * So the confirm says it in advance.
 */
export function siteFallout(
  pages: { scope_id: number; site_id: number | null; site_name: string | null }[],
  scopeId: number,
): SiteFallout {
  const mine = new Map<string, string>();
  const others = new Set<string>();
  for (const page of pages) {
    const key = siteKey(page.site_id);
    if (page.scope_id === scopeId) mine.set(key, page.site_name || 'No site yet');
    else others.add(key);
  }
  const disappearing: string[] = [];
  const shrinking: string[] = [];
  for (const [key, name] of Array.from(mine)) {
    (others.has(key) ? shrinking : disappearing).push(name);
  }
  return { disappearing: disappearing.sort(), shrinking: shrinking.sort() };
}

export const SOURCE_DOT_HEXES = [
  '#6366f1', // indigo-500
  '#14b8a6', // teal-500
  '#d946ef', // fuchsia-500
  '#06b6d4', // cyan-500
  '#8b5cf6', // violet-500
  '#84cc16', // lime-500
];

export function siteColor(index: number): string {
  return SITE_COLORS[index % SITE_COLORS.length];
}


// ── node geometry ────────────────────────────────────────────────────────────
//
// d3-force decides WHERE nodes go. Everything here is how big they are and what
// shape the edges between them take — pure, because geometry that is wrong
// never throws. An arrowhead buried under a node, a label spilling past its
// circle, two opposite edges drawn on top of each other: all render happily and
// all are read as facts about the map.

export const MIN_R = 26;
export const MAX_R = 62;
/** Rough advance width of the label font at 11px. SVG cannot measure text
 *  without a DOM, and a layout that needs the DOM cannot be tested. */
const CHAR_W = 6.2;
const LINE_H = 13;

/**
 * The site name, broken to fit inside its own circle.
 *
 * Names like "my sunscreen site" are three words and 17 characters. On one line
 * that forces a node wide enough to shove the rest of the graph off-screen; two
 * or three short lines keep the node round and the text inside it.
 *
 * Breaks on WORDS, and only mid-word when a single word is longer than the
 * line, because a domain split as "sunscre / en" is unreadable in a way that a
 * slightly wide node is not.
 */
export function wrapLabel(name: string, maxChars = 13, maxLines = 3): string[] {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    if (!line) line = word;
    else if (line.length + 1 + word.length <= maxChars) line += ` ${word}`;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);

  // A single over-long word (a bare domain) still has to fit somehow.
  const split: string[] = [];
  for (const l of lines) {
    if (l.length <= maxChars + 4) split.push(l);
    else
      for (let i = 0; i < l.length; i += maxChars) split.push(l.slice(i, i + maxChars));
  }

  if (split.length <= maxLines) return split;
  const kept = split.slice(0, maxLines);
  kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, maxChars - 1)}…`;
  return kept;
}

/**
 * How big a node has to be: large enough for its label, then larger for a big
 * site.
 *
 * Page count is on a SQUARE-ROOT scale because area is what the eye reads as
 * size — scaled by radius, a 26-page site would look ten times a 3-page one
 * rather than three. But the label wins: a node that cannot hold its own name
 * is worse than one that overstates its size a little.
 */
export function fitRadius(lines: string[], pageCount: number): number {
  const widest = Math.max(...lines.map((l) => l.length)) * CHAR_W;
  // The widest chord a line can occupy inside a circle, allowing for the lines
  // above and below it plus the count underneath.
  const forText = Math.max(widest / 1.7, (lines.length * LINE_H + 16) / 1.5);
  const forSize = MIN_R + Math.sqrt(Math.max(0, pageCount)) * 3;
  return Math.min(MAX_R, Math.max(MIN_R, forText, forSize));
}

/**
 * Stroke width from a link count, so a heavy edge reads as heavy.
 *
 * SCALED ACROSS THE RANGE, not against the maximum. `count / max` drew every
 * edge on a map where the counts are all equal — which is most maps, and every
 * new one — at MAXIMUM width, because each count WAS the maximum. The result
 * was uniformly fat ribbons with the arrowheads lost against them, and a
 * thickness that carried no information while looking like it did.
 *
 * Width now says how far above the LIGHTEST edge this one is. When nothing is
 * heavier than anything else, everything is thin — which is the honest picture
 * rather than an emphatic one.
 */
export function edgeWidth(count: number, min: number, max: number): number {
  if (max <= min) return 1.6;
  return 1.6 + ((count - min) / (max - min)) * 4.4;
}

export interface Positioned {
  x: number;
  y: number;
  r: number;
}

/**
 * The arc between two nodes, trimmed to their rims.
 *
 * AN ARC, NOT A LINE, and the same trick `mobile-patent-suits` uses: a curve
 * whose radius is the distance between the nodes. Two sites that link to each
 * other produce two edges over one pair of points, and drawn straight they
 * coincide — one line with an arrowhead at each end, reading as a single
 * bidirectional link and hiding one of the two counts.
 *
 * Both ends are pulled back by the node's own radius. Node sizes vary here, so
 * a marker `refX` — how the d3 examples do it — cannot work: it is a single
 * fixed offset for every edge.
 */
export interface ArcGeometry {
  d: string;
  labelX: number;
  labelY: number;
  /** Direction of travel at the label, in degrees, for a chevron to point
   *  along. An arrowhead at the far end alone is easy to lose against a node
   *  and a ring; a marker out in the open where the eye already is — on the
   *  count — says which way this goes without hunting for the tip. */
  angle: number;
}

export function linkArc(
  source: Positioned,
  target: Positioned,
  arrow = 9,
): ArcGeometry {
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const dist = Math.hypot(dx, dy) || 1;
  const ux = dx / dist;
  const uy = dy / dist;

  const sx = source.x + ux * source.r;
  const sy = source.y + uy * source.r;
  const tx = target.x - ux * (target.r + arrow);
  const ty = target.y - uy * (target.r + arrow);

  const r = Math.hypot(tx - sx, ty - sy) || 1;
  return {
    d: `M${sx},${sy}A${r},${r} 0 0,1 ${tx},${ty}`,
    // Pushed off the chord toward the arc's belly, so the count sits ON the
    // curve rather than in the gap the curve leaves.
    labelX: (sx + tx) / 2 + uy * r * 0.12,
    labelY: (sy + ty) / 2 - ux * r * 0.12,
    angle: (Math.atan2(ty - sy, tx - sx) * 180) / Math.PI,
  };
}

/**
 * A site linking within itself — a loop off the top of the node.
 *
 * Usually the MAJORITY of links, and with no second node there is no line to
 * draw: without this a site with 40 internal links looks identical to one with
 * none.
 */
export function selfArc(node: Positioned): ArcGeometry {
  const r = node.r;
  const loop = r * 0.62;
  const x1 = node.x - r * 0.42;
  const y1 = node.y - r * 0.86;
  const x2 = node.x + r * 0.52;
  const y2 = node.y - r * 0.8;
  return {
    d: `M${x1},${y1}A${loop},${loop} 0 1,1 ${x2},${y2}`,
    labelX: node.x + r * 0.05,
    labelY: node.y - r - loop * 1.25,
    // The loop is drawn left to right over the top of the node, so travel at
    // its apex is straight along +x.
    angle: 0,
  };
}

// ── fitting the graph to its canvas ──────────────────────────────────────────

export interface View {
  k: number;
  x: number;
  y: number;
}

export const IDENTITY_VIEW: View = { k: 1, x: 0, y: 0 };

/** How much room to leave around the outermost node. */
export const FIT_PADDING = 28;

/**
 * The zoom and offset that put every node on screen at once.
 *
 * A force layout settles at whatever scale its charge and link distance imply,
 * and that has nothing to do with the size of the box it is drawn in — eight
 * sites spread well past the viewBox, so the first thing you saw was a graph
 * you had to zoom out of before you could read it.
 *
 * Solved in the VIEW, not by weakening the forces. Tuning charge until things
 * happen to fit is a constant that stops being true at nine nodes; a transform
 * derived from where the nodes actually are is true at any count.
 *
 * The transform this feeds is
 *   translate(W/2,H/2) scale(k) translate(-W/2+x, -H/2+y)
 * so a point p lands at W/2 + k·(p − W/2 + x). Setting the bounding box's
 * centre to the canvas centre solves to x = W/2 − cx, independent of k — which
 * is why the offset can be computed without knowing the zoom.
 */
export function fitView(
  nodes: { x?: number | null; y?: number | null; r: number }[],
  width: number,
  height: number,
  padding = FIT_PADDING,
  maxZoom = 1.4,
): View {
  const placed = nodes.filter(
    (n) => typeof n.x === 'number' && typeof n.y === 'number',
  ) as { x: number; y: number; r: number }[];
  if (placed.length === 0) return IDENTITY_VIEW;

  // Each node's own radius plus the room its chrome takes: the count line
  // under the name, the LEFT/RIGHT chip above it, and a self-link's loop.
  // Measuring circles alone would clip exactly those, which are the parts that
  // say what the node IS.
  const pad = (n: { r: number }) => n.r * 1.9;
  const minX = Math.min(...placed.map((n) => n.x - pad(n)));
  const maxX = Math.max(...placed.map((n) => n.x + pad(n)));
  const minY = Math.min(...placed.map((n) => n.y - pad(n)));
  const maxY = Math.max(...placed.map((n) => n.y + pad(n)));

  const spanX = Math.max(1, maxX - minX);
  const spanY = Math.max(1, maxY - minY);
  const k = Math.min(
    maxZoom,
    (width - padding * 2) / spanX,
    (height - padding * 2) / spanY,
  );
  return {
    // Capped so a single node does not fill the canvas, and floored so a very
    // wide graph stays legible rather than becoming dust.
    k: Math.max(0.25, k),
    x: width / 2 - (minX + maxX) / 2,
    y: height / 2 - (minY + maxY) / 2,
  };
}
